import { getMessagesDiv } from './chat/dom';
import { logSessionDebug } from './session-debug';

const HISTORY_BANNER_AUTO_HIDE_MS = 1600;
const HISTORY_BANNER_PROGRESS_HIDE_MS = 1200;

export type HistoryBannerStatus = 'hidden' | 'loading' | 'progress' | 'complete' | 'error';

type HistoryBannerSnapshot = {
  status: HistoryBannerStatus;
  loaded: number;
  total: number;
  message: string;
  retryLabel: string;
  retryVisible: boolean;
};

type CreateHistoryBannerControllerOptions = {
  onRetry: () => void;
};

function clampCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

export function buildHistoryBannerSnapshot(
  status: HistoryBannerStatus,
  loadedCount: number,
  totalCount: number,
): HistoryBannerSnapshot {
  const normalizedLoaded = clampCount(loadedCount);
  const normalizedTotal = Math.max(clampCount(totalCount), normalizedLoaded);
  if (status === 'loading') {
    return {
      status,
      loaded: normalizedLoaded,
      total: normalizedTotal,
      message: `正在加载更早消息… 已加载 ${normalizedLoaded} / ${normalizedTotal} 条`,
      retryLabel: '',
      retryVisible: false,
    };
  }
  if (status === 'complete') {
    return {
      status,
      loaded: normalizedLoaded,
      total: normalizedTotal,
      message: `已加载全部历史 · ${normalizedLoaded} / ${normalizedTotal} 条`,
      retryLabel: '',
      retryVisible: false,
    };
  }
  if (status === 'progress') {
    return {
      status,
      loaded: normalizedLoaded,
      total: normalizedTotal,
      message: `已加载更多历史 · ${normalizedLoaded} / ${normalizedTotal} 条`,
      retryLabel: '',
      retryVisible: false,
    };
  }
  if (status === 'error') {
    return {
      status,
      loaded: normalizedLoaded,
      total: normalizedTotal,
      message: `加载更早消息失败 · 已加载 ${normalizedLoaded} / ${normalizedTotal} 条`,
      retryLabel: '点击重试',
      retryVisible: true,
    };
  }
  return {
    status: 'hidden',
    loaded: normalizedLoaded,
    total: normalizedTotal,
    message: '',
    retryLabel: '',
    retryVisible: false,
  };
}

export function createHistoryBannerController(options: CreateHistoryBannerControllerOptions) {
  let bannerEl: HTMLElement | null = null;
  let statusTextEl: HTMLElement | null = null;
  let retryBtnEl: HTMLButtonElement | null = null;
  let hideTimer = 0;
  let current = buildHistoryBannerSnapshot('hidden', 0, 0);

  function clearHideTimer(): void {
    if (!hideTimer) return;
    clearTimeout(hideTimer);
    hideTimer = 0;
  }

  function withScrollPreserved(mutator: (messagesDiv: HTMLElement) => void): void {
    const messagesDiv = getMessagesDiv();
    if (!messagesDiv) return;
    const prevHeight = messagesDiv.scrollHeight;
    const prevScrollTop = messagesDiv.scrollTop;
    mutator(messagesDiv);
    messagesDiv.scrollTop = prevScrollTop + (messagesDiv.scrollHeight - prevHeight);
  }

  function ensureBanner(): void {
    const messagesDiv = getMessagesDiv();
    if (!messagesDiv) return;
    const existing = messagesDiv.querySelector('.history-banner') as HTMLElement | null;
    if (existing) {
      bannerEl = existing;
      statusTextEl = existing.querySelector('.history-banner-status');
      retryBtnEl = existing.querySelector('.history-banner-retry') as HTMLButtonElement | null;
      return;
    }
    withScrollPreserved((container) => {
      const element = document.createElement('div');
      element.className = 'history-banner';
      element.hidden = true;
      element.setAttribute('aria-live', 'polite');
      element.innerHTML = `
        <div class="history-banner-shell">
          <span class="history-banner-dot" aria-hidden="true"></span>
          <span class="history-banner-status"></span>
          <button type="button" class="history-banner-retry" hidden></button>
        </div>
      `;
      container.insertBefore(element, container.firstChild);
      bannerEl = element;
      statusTextEl = element.querySelector('.history-banner-status');
      retryBtnEl = element.querySelector('.history-banner-retry') as HTMLButtonElement | null;
      retryBtnEl?.addEventListener('click', () => options.onRetry());
    });
  }

  function applySnapshot(snapshot: HistoryBannerSnapshot): void {
    const unchanged = (
      current.status === snapshot.status
      && current.loaded === snapshot.loaded
      && current.total === snapshot.total
      && current.message === snapshot.message
      && current.retryVisible === snapshot.retryVisible
      && current.retryLabel === snapshot.retryLabel
    );
    if (unchanged) return;
    ensureBanner();
    if (!bannerEl || !statusTextEl || !retryBtnEl) return;
    current = snapshot;
    clearHideTimer();
    withScrollPreserved(() => {
      bannerEl!.dataset.state = snapshot.status;
      bannerEl!.hidden = snapshot.status === 'hidden';
      statusTextEl!.textContent = snapshot.message;
      retryBtnEl!.hidden = !snapshot.retryVisible;
      retryBtnEl!.textContent = snapshot.retryLabel;
    });
    const rect = bannerEl.getBoundingClientRect();
    logSessionDebug('history-banner:state', {
      status: snapshot.status,
      loaded: snapshot.loaded,
      total: snapshot.total,
      retryVisible: snapshot.retryVisible,
      message: snapshot.message || null,
      dom: {
        hidden: bannerEl.hidden,
        connected: bannerEl.isConnected,
        offsetTop: bannerEl.offsetTop,
        offsetHeight: bannerEl.offsetHeight,
        rectTop: Number.isFinite(rect.top) ? Math.round(rect.top) : null,
        rectBottom: Number.isFinite(rect.bottom) ? Math.round(rect.bottom) : null,
      },
    });
  }

  function show(status: Exclude<HistoryBannerStatus, 'hidden'>, loaded: number, total: number): void {
    applySnapshot(buildHistoryBannerSnapshot(status, loaded, total));
    if (status === 'complete' || status === 'progress') {
      const delay = status === 'complete' ? HISTORY_BANNER_AUTO_HIDE_MS : HISTORY_BANNER_PROGRESS_HIDE_MS;
      logSessionDebug('history-banner:auto-hide-scheduled', {
        afterMs: delay,
        status,
        loaded,
        total,
      });
      hideTimer = window.setTimeout(() => {
        logSessionDebug('history-banner:auto-hide-fired', {
          status,
          loaded,
          total,
        });
        applySnapshot(buildHistoryBannerSnapshot('hidden', loaded, total));
      }, delay);
    }
  }

  return {
    showLoading(loaded: number, total: number): void {
      show('loading', loaded, total);
    },
    showComplete(loaded: number, total: number): void {
      show('complete', loaded, total);
    },
    showProgress(loaded: number, total: number): void {
      show('progress', loaded, total);
    },
    showError(loaded: number, total: number): void {
      show('error', loaded, total);
    },
    hide(): void {
      applySnapshot(buildHistoryBannerSnapshot('hidden', current.loaded, current.total));
    },
    getStatus(): HistoryBannerStatus {
      return current.status;
    },
  };
}
